

import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { CommonContext } from "../../../App";
import { useTranslation } from "react-i18next";
import LoadingSpinner from "../../../Components/CommonComponent/loadingSpiner/LoadingSpinner";
import ContactMarquee from "../../../Components/CommonComponent/Table/ContactMarquee";
import api from "../../../services/api";

export default function GroupsTable() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const types = [{ name: "Liability", nameInHindi: "दायित्व,देनदारी" }, { name: "Assets", nameInHindi: "सम्पत्ति, लेनदारी " }, { name: "Expense", nameInHindi: "खर्च, खरीद" }, { name: "Income", nameInHindi: "कमाई, बिक्री" },];
  const [tableData, setTableData] = useState([]);


  const { branchId, dateRange, setDateRange } = useContext(CommonContext);

  const [loading, setLoading] = useState(false);

  const [startDate, setStartDate] = useState(dateRange.startDate);
  const [endDate, setEndDate] = useState(dateRange.endDate);
  const storedLang = JSON.parse(localStorage.getItem("second_language"));
  const language = storedLang?.language || "en";
  const primeLanguage = localStorage.getItem("selectedLanguage");
  const [focusedIndex, setFocusedIndex] = useState(null);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const containerRef = useRef(null);
  useEffect(() => {
    setStartDate(dateRange.startDate);
    setEndDate(dateRange.endDate);
  }, [dateRange]);
  const flatRows = tableData.flatMap((section, sectionIndex) =>
    section.rows.map((row, rowIndex) => ({
      row,
      sectionIndex,
      rowIndex,
    }))
  );
  const handleKeyDown = (e) => {
    if (!flatRows.length) return;

    //  DOWN
    if (e.key === "ArrowDown") {
      e.preventDefault();

      setFocusedIndex((prev) => {
        if (prev === null) return 0;
        return Math.min(prev + 1, flatRows.length - 1);
      });
    }

    //  UP
    if (e.key === "ArrowUp") {
      e.preventDefault();

      setFocusedIndex((prev) => {
        if (prev === null) return flatRows.length - 1;
        return Math.max(prev - 1, 0);
      });
    }

    // SPACE → select
    if (e.key === " ") {
      e.preventDefault();
      if (focusedIndex === null) return;

      setSelectedRows((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(focusedIndex)) {
          newSet.delete(focusedIndex);
        } else {
          newSet.add(focusedIndex);
        }
        return newSet;
      });
    }

    // ENTER → navigate
    if (e.key === "Enter") {
      e.preventDefault();
      if (focusedIndex !== null) {
        handleRowClick(flatRows[focusedIndex].row);
      }
    }

    // ESC → back
    // if (e.key === "Escape") {
    //   e.preventDefault();
    //   navigate(-1);
    // }
  };
  useEffect(() => {
    containerRef.current?.focus();
  }, []);
  useEffect(() => {
    if (focusedIndex === null) return;

    const el = document.getElementById(`row-${focusedIndex}`);
    el?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [focusedIndex]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const [ledgersRes, summaryRes] = await Promise.all([
          api.get(`ledgers/get-all-ledger?branchId=${branchId}`),
          api.get(`ledgers/get-balance-sheet`, {
            params: { startDate, endDate, branchId },
          }),
        ]);

        if (!ledgersRes.data.success || !summaryRes.data.success) {
          setLoading(false);
          return;
        }

        const allLedgers = ledgersRes.data.allData;
        const allSummary = summaryRes.data.data.flatMap(
          (branch) => branch.branchData || []
        );

        // console.log('All Summary Data:', allSummary); // Debug log

        const result = types.map((type) => {
          const filteredLedgers = allLedgers.filter((l) => l.groupType === type.name);

          const groupMap = new Map();
          filteredLedgers.forEach((item) => {
            if (!groupMap.has(item.groupName)) {
              groupMap.set(item.groupName, { ...item });
            }
          });

          const uniqueGroups = Array.from(groupMap.values());

          const rows = uniqueGroups.map((group) => {
            const summaries = allSummary.filter(
              (s) =>
                s.groupType === group.groupType &&
                s.groupName === group.groupName
            );

            // console.log(`Group: ${group.groupName}, Type: ${group.groupType}, Summaries:`, summaries); // Debug log

            // Opening = previousReceived - previousPaid
            // Opening = openingReceived - openingPaid
            const openingReceived = summaries.reduce(
              (sum, s) => sum + Math.abs(s.openingReceived || 0),
              0
            );
            const openingPaid = summaries.reduce(
              (sum, s) => sum + Math.abs(s.openingPaid || 0),
              0
            );
            const openingQty = summaries.reduce((sum, s) => sum + Math.abs(s.prevQuantity || 0), 0);
            const currentQty = summaries.reduce((sum, s) => sum + Math.abs(s.totalQuantity || 0), 0);
            let openingBalance = Math.abs(openingReceived - openingPaid);
            const openingType = openingReceived > openingPaid ? "Dr" : "Cr";

            // Hide opening for Income / Expense
            const showOpening =
              group.groupType === "Expense" || group.groupType === "Income"
                ? "-"
                : `${openingBalance.toLocaleString()} ${openingType}`;

            // Current Period Debit / Credit
            const totalDebit = summaries.reduce(
              (sum, s) => sum + Math.abs(s.currentReceived || 0),
              0
            );
            const totalCredit = summaries.reduce(
              (sum, s) => sum + Math.abs(s.currentPaid || 0),
              0
            );

            // Final Closing = opening + debit - credit
            // For Assets/Liability: include opening in calculation
            // For Income/Expense: only use current period
            let closingBalance;
            let closingType;

            if (group.groupType === "Assets" || group.groupType === "Liability") {
              // Include opening balance
              const openingWithSign = openingReceived - openingPaid;
              const netChange = totalDebit - totalCredit;
              const closingWithSign = openingWithSign + netChange;

              closingBalance = Math.abs(closingWithSign);
              closingType = closingWithSign >= 0 ? "Dr" : "Cr";
            } else {
              // Income/Expense: only current period
              closingBalance = Math.abs(totalDebit - totalCredit);
              closingType = totalDebit > totalCredit ? "Dr" : "Cr";
            }

            return {
              ...group,
              openingBalanceValue: openingBalance,
              openingType,
              showOpening,
              totalDebit,
              totalCredit,
              totalClosing: closingBalance,
              closingType,
              openingQty,
              currentQty,
            };
          });

          // Remove rows where ALL values are 0 (opening, debit, credit, closing)
          // const nonZeroRows = rows.filter(
          //   (r) =>
          //     r.openingBalanceValue !== 0 ||
          //     r.totalDebit !== 0 ||
          //     r.totalCredit !== 0 ||
          //     r.totalClosing !== 0 
          // );
          const nonZeroRows = rows.filter((r) => {
            if (type.name === "Income" || type.name === "Expense") {
              // Only show if current period transaction exists
              return r.totalDebit !== 0 || r.totalCredit !== 0;
            }

            // Assets / Liability
            return (
              r.openingBalanceValue !== 0 ||
              r.totalDebit !== 0 ||
              r.totalCredit !== 0 ||
              r.totalClosing !== 0
            );
          });

          // console.log(`Type: ${type}, Non-zero rows:`, nonZeroRows); // Debug log

          // TOTALS
          const totalOpening = nonZeroRows.reduce(
            (sum, r) =>
              sum +
              (r.openingBalanceValue *
                (r.openingType === "Dr" ? 1 : r.openingType === "Cr" ? -1 : 0)),
            0
          );
          const totalOpeningQty = nonZeroRows.reduce((s, r) => s + r.openingQty, 0);
          const totalCurrentQty = nonZeroRows.reduce((s, r) => s + r.currentQty, 0);

          const totalDebit = nonZeroRows.reduce((sum, r) => sum + r.totalDebit, 0);
          const totalCredit = nonZeroRows.reduce(
            (sum, r) => sum + r.totalCredit,
            0
          );
          // Signed Opening
          const totalOpeningSigned = nonZeroRows.reduce(
            (sum, r) =>
              sum +
              (r.openingBalanceValue *
                (r.openingType === "Dr" ? 1 : r.openingType === "Cr" ? -1 : 0)),
            0
          );

          // Final Closing = Opening + Debit - Credit
          let totalClosingValue = 0;
          let totalClosingType = "";

          if (type.name === "Income" || type.name === "Expense") {
            const signed = totalDebit - totalCredit;

            totalClosingValue = Math.abs(signed);
            totalClosingType = signed > 0 ? "Dr" : signed < 0 ? "Cr" : "";

          } else {
            const finalClosingSigned =
              totalOpeningSigned + totalDebit - totalCredit;

            totalClosingValue = Math.abs(finalClosingSigned);
            totalClosingType =
              finalClosingSigned > 0
                ? "Dr"
                : finalClosingSigned < 0
                  ? "Cr"
                  : "";
          }

          return {
            type,
            rows: nonZeroRows,
            totalOpening: Math.abs(totalOpening),
            openingType: totalOpening > 0 ? "Dr" : "Cr",
            totalDebit,
            totalCredit,
            totalClosing: totalClosingValue,
            closingType: totalClosingType,
            totalOpeningQty,
            totalCurrentQty
          };
        });

        setTableData(result);
      } catch (err) {
        console.error("Error:", err);
      } finally {
        setLoading(false);
      }
    };
    if (startDate && endDate && branchId) {
      fetchData();
    }
  }, [startDate, endDate, branchId]);

  const handleRowClick = (row) => {
    navigate("/account-books/sub-group-table", { state: { ledger: row } });
  };
  let globalIndex = -1;

  return (
    <div className="container-fluid ">
      <div ref={containerRef}
        tabIndex={0}
        onKeyDown={handleKeyDown}
        className="outline-none ">
        <div className="row pb-5 p-0">

          {tableData.map((section, idx) => (
            <div className="col-xl-6 my-2 mt-2" key={idx}>
              <div>
                <div className="flex justify-between border-b  bg-theme-headerBg text-theme-color px-2 ">
                  <div className="text-sm font-medium py-[2px]">
                    {/* {(primeLanguage === "en" )|| (primeLanguage === "pa")
                      ? `${t("groupsOf")} ${section.type.name} (${section.type.nameInHindi})`
                      : `${section.type.nameInHindi} ${t("groupsOf")}`  
                    } */}
                    {
                      // (primeLanguage === "en" || primeLanguage === "pn")
                      //   ? `${t("groupsOf")} ${section.type.name}`
                      //   : `${section.type.nameInHindi} ${t("groupsOf")}`
                      primeLanguage === "en"
                        ? `${t("groupsOf")} ${section.type.name} (${section.type.nameInHindi})`
                        : primeLanguage === "pn"
                          ? `${t("groupsOf")} ${section.type.name}`
                          : `${section.type.nameInHindi} ${t("groupsOf")}`
                    }
                  </div>
                </div>

                <div>
                  <table className="min-w-full bg-white table-fixed  border-separate border-spacing-0  text-sm capitalize">
                    <thead>
                      <tr className="bg-white text-theme-headerBg border-b border-1 border-theme-headerBg ">
                        <th className=" px-1 py-[2px] font-medium border-b   text-center">{t('srNumber')}</th>
                        <th className=" px-1 font-medium text-start border-b  w-40 ">{t("groupName")}</th>

                        {/* Hide Opening column for Expense & Income */}
                        {section.type.name !== "Expense" && section.type.name !== "Income" && (
                          <th className=" px-1 font-medium border-b   text-end">
                            {t("openingBalance")}
                          </th>
                        )}
                        {/* {section.type !== "Assets" && section.type !== "Liability" && <>
                    <th className="border px-1 font-medium   text-center">{t("openingQty")}</th>
                    <th className="border px-1 font-medium   text-center">{t("closingQty")}</th>
                    </>} */}


                        <th className=" px-1  font-medium border-b   text-end">{t("receivedDr")}</th>
                        <th className=" px-1 font-medium border-b   text-end">{t("paidCr")}</th>
                        {section.type.name !== "Expense" && section.type.name !== "Income" ? (
                          <th className=" px-1 font-medium border-b   text-end">
                            {t("closingBalance")}
                          </th>
                        ) : (
                          <th className=" px-1 font-medium border-b   text-end">{t("total")}</th>
                        )}
                      </tr>
                    </thead>

                    <tbody>
                      {section.rows.length > 0 ? (
                        section.rows.map((row, i) => {
                          globalIndex++;
                          return (
                            <tr
                              id={`row-${globalIndex}`}
                              key={i}
                              onClick={() => handleRowClick(row)}
                              className={`cursor-pointer
  ${focusedIndex === globalIndex
                                  ? "bg-[#5c5b5b] text-white"   // FOCUS (highest priority)
                                  : selectedRows.has(globalIndex)
                                    ? "bg-[#f4c48a] text-black"   // SELECTED (darker than before)
                                    : ""
                                }
`}
                            >
                              <td className="border-b px-1 text-center">{i + 1}</td>
                              <td className="border-b px-1 max-w-32  text-wrap">
                                {/* {row.groupName} {row.groupNameInHindi} */}
                                <ContactMarquee firstName={row.groupName} secondName={row.groupNameInHindi} />
                              </td>

                              {/* Hide Opening value for Expense & Income */}
                              {section.type.name !== "Expense" && section.type.name !== "Income" && (
                                <td className="border-b px-1 text-end">{row.showOpening}</td>
                              )}
                              {/* {section.type !== "Assets" && section.type !== "Liability" && (
                         <>
                         <td className="border px-1 text-end">{row.openingQty}</td>
                           <td className="border px-1 text-end">{row.currentQty}</td>
                         </>
                        )} */}

                              <td className="border-b py-[2px] px-1 text-end">
                                {row.totalDebit.toLocaleString()}
                              </td>
                              <td className="border-b px-1 text-end">
                                {row.totalCredit.toLocaleString()}
                              </td>

                              <td className="border-b px-1 text-end">
                                {row.totalClosing.toLocaleString()}{" "}
                                {section.type.name !== "Expense" &&
                                  section.type.name !== "Income" &&
                                  row.closingType}
                              </td>
                            </tr>
                          )
                        })
                      ) : (
                        <tr>
                          <td
                            colSpan="6"
                            className="border-b px-4 py-4 text-center text-sm text-gray-500 "
                          >
                            {t("noDataAvailable")}
                          </td>
                        </tr>
                      )}
                    </tbody>

                    {/* FOOTER TOTALS */}
                    {section.rows.length > 0 && (
                      <tfoot>
                        <tr className="bg-white border-1  border-theme-headerBg">
                          <td colSpan="2" className="border-b py-[2px] ps-3 font-semibold">
                            {t("total")}
                          </td>

                          {section.type.name !== "Expense" && section.type.name !== "Income" && (
                            <td className="border-b px-1 text-end font-semibold">
                              {section.totalOpening.toLocaleString()} {section.openingType}
                            </td>
                          )}
                          {/* {section.type !== "Assets" && section.type !== "Liability" && (
                         <>
                         <td className="border px-1 text-end font-medium">{section.totalOpeningQty}</td>
                           <td className="border px-1 text-end font-medium" >{section.totalCurrentQty}</td>
                         </>
                        )} */}

                          <td className="border-b px-1 text-end font-semibold">
                            {section.totalDebit.toLocaleString()}
                          </td>
                          <td className="border-b px-1 text-end font-semibold">
                            {section.totalCredit.toLocaleString()}
                          </td>

                          <td className="font-semibold border-b px-1  text-end ">
                            {section.totalClosing.toLocaleString()}{" "}
                            {section.type.name !== "Expense" &&
                              section.type.name !== "Income" &&
                              section.closingType}
                          </td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {loading && (
        <div className="flex justify-center my-4">
          <LoadingSpinner size={70} color='#be8024' />
        </div>
      )}
    </div>
  );
}
